import { animate, AnimationEvent, state, style, transition, trigger } from "@angular/animations";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  TemplateRef
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Logger } from "../../services/logger.service";
import { SlideEvent, SlideState } from './models';
import { Side, SlidePanelDirective } from "./slide-panel.directive";

@Component({
    selector: 'slide-panel',
    templateUrl: './slide-panel.component.html',
    styleUrls: ['./slide-panel.component.scss'],
    animations: [
        trigger('slide', [
            state('left', style({ transform: 'translateX(0)' })),
            state('right', style({ transform: 'translateX({{offset}}%)' }), { params: { offset: '0' } }),
            transition('* => *', animate('{{ time }}'))
        ])
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SlidePanelComponent implements OnInit {

  @ContentChildren(SlidePanelDirective, { descendants: false }) slidePanels!: QueryList<SlidePanelDirective>;

  Side = Side;

  @Input() set id(id: string) {
    if (id != this.id) {
      this._id = id;
      this.setId = true;
      this.slidePanel = this.findSlidePanel(id);
      if (this.slidePanel?.templateRef) {
        this.updateOrCreateTemplate(this.slidePanel);
      }
      this.changeDetector.markForCheck();
    }
  }

  get id(): string | undefined {
    return this._id;        
  }

  @Input() animate  = true;
  @Output() onSlide = new EventEmitter<SlideEvent>();

  public _id: string | undefined;
  protected setId = false;

  public slidePanel: SlidePanelDirective | undefined;
  public templates: { [side: string]: TemplateRef<any> } = {};
  public sliding = false;

  protected logger = new Logger('SlidePanelComponent');

  constructor(private changeDetector: ChangeDetectorRef,
              private destroyRef: DestroyRef) {
  }

  ngOnInit(): void {
  }

  ngAfterContentInit() {
    const update = (slidePanels: QueryList<SlidePanelDirective>) => {
      let fallback: SlidePanelDirective | undefined = undefined;
      let current: SlidePanelDirective | undefined = undefined;
      slidePanels
        ?.filter(slidePanel => slidePanel?.templateRef)
        .forEach((slidePanel, index) => {
          let render = !slidePanel.lazy;
          if (!this.setId && index==0) {  // if id was not explicitly assigned yet
            this._id = slidePanel.id;     // take the id of the first slide
          }
          if (!fallback && slidePanel.id=='*') {
            fallback = slidePanel;
          }
          if (this.id && !current) {
            const last = index==this.slidePanels.length-1;
            current = this.id==slidePanel.id ? slidePanel : last ? fallback || slidePanels.first : undefined;
            if (current) {
              render = true;
              // modification of template bound property SlidePanelContainerComponent.slidePanels
              // is scheduled to run  after the current change detection cycle has completed
              // in order to avoid ExpressionChangedAfterItHasBeenCheckedError
              // this also helps to prevent rendering until the component is fully initialized
              window.setTimeout(() => {
                // not safe to use current here - the value of this.id value may have been changed from outside
                this.slidePanel = this.findSlidePanel(this.id); // this.slidePanel = current;
                this.changeDetector.detectChanges();
              });
            }
          }
          if (render) {
            this.updateOrCreateTemplate(slidePanel);
          }
        });
    };
    update(this.slidePanels);
    this.slidePanels.changes
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(changes => {
        // this.logger.info('SLIDE PANEL CHANGES', changes);
        update(changes);
      })
  }

  protected findSlidePanel(id: string | undefined) {
    if (this.slidePanels?.length) {
      const find = (id: string | undefined): SlidePanelDirective | undefined => {
        const slidePanel = this.slidePanels.find(slidePanel => slidePanel.id == id);
        return !slidePanel && id!='*' ? find('*') : slidePanel;
      };
      return find(id);
    }
    return undefined;
  }

  protected updateOrCreateTemplate(slidePanel: SlidePanelDirective) {
    const side = slidePanel.side || Side.LEFT;
    this.templates[side] = slidePanel.templateRef;
    this.changeDetector.detectChanges();
  }

  is(id: string): boolean {
    return this.id == id;
  }

  toggle() {
    this._id = this.slidePanels?.toArray()?.find(slidePanel => slidePanel.id!=this.id)?.id || undefined;
  }

  get slideState(): any {
    const state = {
      value: this.slidePanel?.side,
      params: {
        time: this.animate ? '300ms' : '0ms'
      } as any
    };
    if (state.value==Side.RIGHT) {
      state.params.offset = 100/Object.keys(this.templates).length - 100;
    }
    return state;
  }

  onSlideStart(event: AnimationEvent) {
    this.sliding = true;
    const  slidePanelIdIn = this.slidePanels.find(slidePanel => slidePanel.side == event.toState)?.id   || event.toState;
    const slidePanelIdOut = this.slidePanels.find(slidePanel => slidePanel.side == event.fromState)?.id || event.fromState;
    this.onSlide.emit({ slideIn: slidePanelIdIn, slideOut: slidePanelIdOut, state: SlideState.START });
  }

  onSlideEnd(event: AnimationEvent) {
    this.sliding = false;
    const  slidePanelIdIn = this.slidePanels.find(slidePanel => slidePanel.side == event.toState)?.id   || event.toState;
    const slidePanelIdOut = this.slidePanels.find(slidePanel => slidePanel.side == event.fromState)?.id || event.fromState;
    this.onSlide.emit({ slideIn: slidePanelIdIn, slideOut: slidePanelIdOut, state: SlideState.END });
  }
}